import { ErrorState, LoadingState, StaleBanner } from '../../components/ui/AsyncState';
import { codeLabel } from '../../utils/codeLabel';
import { useRecoveryQuery as useQuery } from '../../components/ui/useRecoveryQuery';
import { operationalQueryPolicy } from '../../api/queryPolicies';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDashboard } from '../../api/endpoints/admin';
import { KpiCard } from '../../components/ui/KpiCard';
import { SectionCard } from '../../components/ui/SectionCard';
import { shipmentStatusLabel } from '../shipments/shipmentStatusLabel';
import { StatusPill } from '../../components/ui/StatusPill';
import { shipmentStatusColor, documentStatusColor } from '../../components/domain/statusColor';
import type { ShipmentStatus, DocumentStatus } from '../../api/types';
import { VehicleCell } from '../../components/domain/VehicleCell';
import styles from './DashboardView.module.css';
import { KpiIcon } from '../../components/ui/KpiIcon';
import { RouteText } from '../../components/domain/RouteText';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { documentSubject } from '../documents/documentSubject';
import { formatIst } from '../shipments/delayReason';
import { stopReasonLabel } from '../shipments/stopLabels';

function fmt(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

export function DashboardView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: getDashboard,
    ...operationalQueryPolicy,
  });

  if (isLoading) return <LoadingState label={t('dashboard.dashboardView.loading')} />;
  if (!data)
    return <ErrorState title={t('dashboard.dashboardView.loadError')} onRetry={() => refetch()} />;

  return (
    <div className={styles.page}>
      {isError && <StaleBanner onRetry={() => refetch()} />}
      {/* KPI row */}
      <div className={styles.kpiRow}>
        <KpiCard
          label={t('dashboard.dashboardView.activeShipments')}
          value={data.activeShipmentsCount}
          icon={<KpiIcon name="truck" />}
          accent="green"
          onClick={() => navigate('/shipments')}
        />
        <KpiCard
          label={t('dashboard.dashboardView.stopped')}
          value={data.stoppedCount}
          icon={<KpiIcon name="warning" />}
          accent="amber"
          onClick={() => navigate('/stopped')}
        />
        <KpiCard
          label={t('dashboard.dashboardView.pendingDocuments')}
          value={data.pendingDocumentsCount}
          icon={<KpiIcon name="document" />}
          accent="amber"
          onClick={() => navigate('/documents')}
        />
      </div>

      {/* Owner review cards (#297): only when the server sends some. */}
      {(data.reviewCards?.length ?? 0) > 0 && (
        <SectionCard title={t('dashboard.dashboardView.needsReview')}>
          <ul className={styles.reviewList}>
            {data.reviewCards!.map((card) => (
              <li key={`${card.kind}-${card.lane}`} className={styles.reviewItem}>
                <StatusPill
                  bg="var(--status-amber-bg)"
                  text="var(--status-amber-text)"
                  label={t('dashboard.dashboardView.review')}
                  icon={<StatusIcon name="hourglass" />}
                />
                <span>
                  {t('dashboard.dashboardView.laneCapReview', { lane: card.lane, overCap: card.overCapCount, days: card.windowDays })}
                </span>
                <Link className={styles.link} to="/settings/approval-limits">
                  {t('dashboard.dashboardView.reviewLimits')}
                </Link>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      <div className={styles.grid}>
        {/* Recent shipments */}
        <SectionCard
          title={t('dashboard.dashboardView.recentShipments')}
          action={
            <Link className={styles.link} to="/shipments">
              {t('dashboard.dashboardView.seeAll')}
            </Link>
          }
        >
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('dashboard.dashboardView.colId')}</th>
                <th>{t('dashboard.dashboardView.colOriginDest')}</th>
                <th>{t('dashboard.dashboardView.colPickup')}</th>
                <th>{t('dashboard.dashboardView.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {data.recentShipments.slice(0, 6).map((s) => {
                const c = shipmentStatusColor(s.status as ShipmentStatus);
                return (
                  <tr key={s.id}>
                    <td className={styles.mono}>#{s.id}</td>
                    <td>
                      <RouteText route={s} className={styles.routeLine} />
                      {s.assignment?.vehicleRegistrationNumber && (
                        <div className={styles.routeVehicle}>
                          <VehicleCell assignment={s.assignment} />
                        </div>
                      )}
                    </td>
                    <td>{s.pickupDate ? fmt(s.pickupDate) : '—'}</td>
                    <td>
                      <StatusPill bg={c.bg} text={c.text} label={shipmentStatusLabel(s.status)} title={s.status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </SectionCard>

        {/* Stopped trucks (dnm-work#165 C5): oldest stop first, as the server sends them */}
        <SectionCard
          title={t('dashboard.dashboardView.stopped')}
          action={
            <Link className={styles.link} to="/stopped">
              {t('dashboard.dashboardView.seeAll')}
            </Link>
          }
        >
          {data.topStopped.length === 0 ? (
            <p className={styles.empty}>{t('dashboard.dashboardView.noStopped')}</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('dashboard.dashboardView.colId')}</th>
                  <th>{t('dashboard.dashboardView.colRoute')}</th>
                  <th>{t('dashboard.dashboardView.colReason')}</th>
                  <th>{t('dashboard.dashboardView.colSince')}</th>
                </tr>
              </thead>
              <tbody>
                {data.topStopped.slice(0, 5).map((s) => (
                  <tr key={s.id}>
                    <td className={styles.mono}>#{s.id}</td>
                    <td>
                      <RouteText route={s} className={styles.routeLine} />
                      {s.assignment?.vehicleRegistrationNumber && (
                        <div className={styles.routeVehicle}>
                          <VehicleCell assignment={s.assignment} />
                        </div>
                      )}
                    </td>
                    <td className={styles.reasonText} title={s.openStop?.note ?? undefined}>
                      {stopReasonLabel(t, s.openStop?.reason)}
                    </td>
                    <td className={styles.mono}>{s.openStop ? formatIst(s.openStop.startedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>

        {/* Pending documents */}
        <SectionCard
          title={t('dashboard.dashboardView.pendingDocuments')}
          action={
            <Link className={styles.link} to="/documents">
              {t('dashboard.dashboardView.seeAll')}
            </Link>
          }
        >
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('dashboard.dashboardView.colId')}</th>
                <th>{t('dashboard.dashboardView.colShipmentVehicle')}</th>
                <th>{t('dashboard.dashboardView.colType')}</th>
                <th>{t('dashboard.dashboardView.colStatus')}</th>
                <th>{t('dashboard.dashboardView.review')}</th>
              </tr>
            </thead>
            <tbody>
              {data.topPendingDocuments.slice(0, 5).map((d) => {
                const c = documentStatusColor(d.status as DocumentStatus);
                const subject = documentSubject(d);
                return (
                  <tr key={d.id}>
                    <td className={styles.mono}>#{d.id}</td>
                    <td>
                      {subject.kind === 'shipment' ? (
                        <Link
                          className={styles.reviewLink}
                          to={`/shipments/${subject.shipmentId}?tab=documents&documentId=${d.id}`}
                        >
                          #{subject.shipmentId}
                        </Link>
                      ) : subject.kind === 'vehicle' ? (
                        subject.label
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{codeLabel('documentType', d.type)}</td>
                    <td>
                      <StatusPill bg={c.bg} text={c.text} label={codeLabel('documentStatus', d.status)} />
                    </td>
                    <td>
                      {subject.kind === 'shipment' ? (
                        <Link
                          className={styles.reviewLink}
                          to={`/shipments/${subject.shipmentId}?tab=documents&documentId=${d.id}`}
                          aria-label={`Review document #${d.id} in shipment #${subject.shipmentId}`}
                        >
                          {t('dashboard.dashboardView.reviewInShipment')}
                        </Link>
                      ) : (
                        <Link
                          className={styles.reviewLink}
                          to={`/documents?status=${d.status === 'UPLOADED' ? 'UPLOADED' : 'PENDING'}`}
                          aria-label={
                            subject.kind === 'vehicle'
                              ? `Review document #${d.id} for ${subject.label}`
                              : `Review document #${d.id}`
                          }
                        >
                          {t('dashboard.dashboardView.review')}
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </SectionCard>
      </div>
    </div>
  );
}
